
import { PaymentStatusChange } from '@/types/communication';
import { toast } from 'sonner';
import { communicationService } from '@/services/communication/communicationService';

class PaymentTrackingService {
  private storageKey = 'nalc_payment_status_changes';

  private async getChanges(): Promise<PaymentStatusChange[]> {
    const changes = localStorage.getItem(this.storageKey);
    return changes ? JSON.parse(changes) : [];
  }

  async trackStatusChange(change: Omit<PaymentStatusChange, 'id' | 'changedAt'>): Promise<PaymentStatusChange> {
    try {
      const changes = await this.getChanges();
      
      // Ensure studentId is a number
      const studentId = typeof change.studentId === 'string' 
        ? parseInt(change.studentId, 10) 
        : change.studentId;
      
      const newChange: PaymentStatusChange = {
        ...change,
        studentId: studentId,
        id: crypto.randomUUID(),
        changedAt: new Date().toISOString(),
        changedBy: change.changedBy || 'Administrator' // Ensure we always have a user
      };

      changes.push(newChange);
      localStorage.setItem(this.storageKey, JSON.stringify(changes));
      
      // Log the payment status change as a communication
      await communicationService.logCommunication({
        type: 'notification',
        message: `Payment status changed from ${change.previousStatus} to ${change.newStatus}`,
        recipientId: studentId.toString(),
        recipientInfo: {},
        content: {
          message: `Payment status changed from ${change.previousStatus} to ${change.newStatus}`
        },
        status: 'sent',
        metadata: {
          category: 'payment',
          priority: 'high'
        }
      });

      return newChange;
    } catch (error) {
      console.error('Error tracking payment status change:', error);
      throw error;
    }
  }

  async getPaymentStatusHistory(
    studentId?: number | string,
    programId?: string
  ): Promise<PaymentStatusChange[]> {
    try {
      const changes = await this.getChanges();
      
      // Convert studentId to number for comparison if it's provided
      const parsedStudentId = studentId !== undefined && typeof studentId === 'string' 
        ? parseInt(studentId, 10) 
        : studentId;
      
      let filteredChanges = changes;
      
      // Filter by student ID if provided
      if (parsedStudentId !== undefined) {
        filteredChanges = filteredChanges.filter(change => change.studentId === parsedStudentId);
      }
      
      // Filter by program ID if provided
      if (programId) {
        filteredChanges = filteredChanges.filter(change => change.programId === programId);
      }
      
      // Sort by date (newest first)
      return filteredChanges.sort((a, b) => 
        new Date(b.changedAt).getTime() - new Date(a.changedAt).getTime()
      );
    } catch (error) {
      console.error('Error getting payment status history:', error);
      throw error;
    }
  }
  
  // Function to get all payment history
  async getAllPaymentHistory(): Promise<PaymentStatusChange[]> {
    try {
      return this.getChanges();
    } catch (error) {
      console.error('Error getting all payment history:', error);
      return [];
    }
  }
  
  // Function to clear all payment history
  async clearPaymentHistory(): Promise<void> {
    try {
      localStorage.removeItem(this.storageKey);
    } catch (error) {
      console.error('Error clearing payment history:', error);
      throw error;
    }
  }
}

export const paymentTrackingService = new PaymentTrackingService();
